import { Bell, BellOff } from "lucide-react";
import { useGetIdentity, useStore } from "ra-core";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import {
  NOTIFICATIONS_ENABLED_STORE_KEY,
  saveUiPreferences,
} from "./userPreferences";

export const PushNotificationPanel = () => {
  const { data: identity, refetch } = useGetIdentity();
  const salesId = (identity?.id as number | null) ?? null;
  const [prefEnabled, setPrefEnabled] = useStore<boolean>(
    NOTIFICATIONS_ENABLED_STORE_KEY,
    true,
  );
  const {
    permissionState,
    isSubscribed,
    isLoading,
    error,
    subscribe,
    unsubscribe,
  } = usePushNotifications(salesId);

  const persistPreference = async (enabled: boolean) => {
    setPrefEnabled(enabled);
    try {
      await saveUiPreferences({ notificationsEnabled: enabled });
      void refetch();
    } catch {
      setPrefEnabled(!enabled);
    }
  };

  const turnOn = async () => {
    await persistPreference(true);
    await subscribe();
  };

  const turnOff = async () => {
    await persistPreference(false);
    if (isSubscribed) {
      await unsubscribe();
    }
  };

  return (
    <Card id="notifications">
      <CardContent className="space-y-4">
        <h2 className="text-xl font-semibold text-muted-foreground">
          Task Notifications
        </h2>

        <Separator />

        {permissionState === "unsupported" ? (
          <p className="text-sm text-muted-foreground">
            Push notifications are not supported in this browser. Try Chrome,
            Edge, or Firefox — or install the app via &quot;Add to Home
            Screen&quot;.
          </p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Receive a notification 30 minutes before a task is due on this
              device. You can snooze individual notifications for 1 hour
              directly from the notification.
            </p>

            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <p className="text-sm font-medium">Account preference</p>
                <p className="text-xs text-muted-foreground">
                  Saved to your profile. Each device still needs browser
                  permission.
                </p>
              </div>
              <Switch
                checked={prefEnabled}
                disabled={isLoading}
                onCheckedChange={(checked) => {
                  void (checked ? turnOn() : turnOff());
                }}
              />
            </div>

            {permissionState === "denied" && (
              <p className="text-sm text-destructive">
                Notifications are blocked in your browser. Click the lock icon
                in the address bar and allow notifications for this site, then
                try again.
              </p>
            )}

            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="flex items-center gap-3">
              {isSubscribed ? (
                <>
                  <Bell className="h-5 w-5 text-green-600 shrink-0" />
                  <span className="text-sm flex-1">
                    Notifications are <strong>enabled</strong> on this device.
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isLoading}
                    onClick={() => void turnOff()}
                  >
                    <BellOff className="h-4 w-4 mr-1.5" />
                    Turn off
                  </Button>
                </>
              ) : (
                <>
                  <BellOff className="h-5 w-5 text-muted-foreground shrink-0" />
                  <span className="text-sm text-muted-foreground flex-1">
                    Notifications are <strong>off</strong> on this device.
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    disabled={isLoading || permissionState === "denied"}
                    onClick={() => void turnOn()}
                  >
                    <Bell className="h-4 w-4 mr-1.5" />
                    Enable notifications
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
